import { generateTestId } from "@client-shared";
import type { Service } from "@shared/types";
import { z } from "zod";

const TEST_ID_PREFIX = "ShopDomainModal";

export const SHOP_DOMAIN_MODAL_TEST_IDS = {
  container: generateTestId(TEST_ID_PREFIX, "container"),
  title: generateTestId(TEST_ID_PREFIX, "title"),
  description: generateTestId(TEST_ID_PREFIX, "description"),
  authMethodSelect: generateTestId(TEST_ID_PREFIX, "authMethodSelect"),
  shopInput: generateTestId(TEST_ID_PREFIX, "shopInput"),
  clientIdInput: generateTestId(TEST_ID_PREFIX, "clientIdInput"),
  clientSecretInput: generateTestId(TEST_ID_PREFIX, "clientSecretInput"),
  clientSecretToggle: generateTestId(TEST_ID_PREFIX, "clientSecretToggle"),
  clientSecretError: generateTestId(TEST_ID_PREFIX, "clientSecretError"),
  accessTokenInput: generateTestId(TEST_ID_PREFIX, "accessTokenInput"),
  accessTokenToggle: generateTestId(TEST_ID_PREFIX, "accessTokenToggle"),
  accessTokenError: generateTestId(TEST_ID_PREFIX, "accessTokenError"),
  authKeyInput: generateTestId(TEST_ID_PREFIX, "authKeyInput"),
  helpPanel: generateTestId(TEST_ID_PREFIX, "helpPanel"),
  languageSelect: generateTestId(TEST_ID_PREFIX, "languageSelect"),
  languageInput: generateTestId(TEST_ID_PREFIX, "languageInput"),
  fieldError: generateTestId(TEST_ID_PREFIX, "field-error"),
  formError: generateTestId(TEST_ID_PREFIX, "form-error"),
  connectButton: generateTestId(TEST_ID_PREFIX, "connectButton"),
  cancelButton: generateTestId(TEST_ID_PREFIX, "cancelButton"),
} as const;

export const SHOPIFY_DOMAIN_SUFFIX = ".myshopify.com";

// Lenient lower bounds for the Shopify credential fields. Kept deliberately
// loose — Shopify's exact lengths change over time and differ per credential
// type — so these only catch obviously-too-short input, not exact formats.
export const CLIENT_ID_MIN_LENGTH = 5;
export const CLIENT_SECRET_MIN_LENGTH = 10;
export const ACCESS_TOKEN_MIN_LENGTH = 5;

const SHOPIFY_STORE_NAME_PATTERN = /^[a-z0-9][a-z0-9-]{1,}[a-z0-9]$/i;

const HTTP_PROTOCOL_PATTERN = /^https?:\/\//i;

const DOMAIN_PATTERN = /^(?!-)[A-Za-z0-9-]{1,63}(?<!-)(\.[A-Za-z0-9-]{1,63})+$/;

/**
 * Strip the .myshopify.com suffix and any protocol/trailing slash so the
 * user can paste the full URL without breaking the install request.
 */
export function normaliseShopifyShop(rawValue: string): string {
  const trimmed = rawValue.trim().toLowerCase();
  if (!trimmed) return "";

  const withoutProtocol = trimmed.replace(HTTP_PROTOCOL_PATTERN, "");
  const withoutTrailingSlash = withoutProtocol.replace(/\/+$/, "");

  return withoutTrailingSlash.endsWith(SHOPIFY_DOMAIN_SUFFIX)
    ? withoutTrailingSlash.slice(0, -SHOPIFY_DOMAIN_SUFFIX.length)
    : withoutTrailingSlash;
}

/**
 * Trim whitespace and remove any trailing slashes from the user-entered
 * WooCommerce shop value. Protocol stripping is deferred to the validation
 * step so the install request payload preserves `https://` exactly as the
 * user entered it.
 */
export function normaliseWooCommerceShop(rawValue: string): string {
  const trimmed = rawValue.trim();
  if (!trimmed) return "";
  return trimmed.replace(/\/+$/, "");
}

/**
 * Resolve a user-facing message from a caught submit error. Falls back to the
 * translated generic message when the error carries no meaningful text — an
 * `Error` with an empty `message`, a blank thrown string, or a non-error value
 * would otherwise surface as a blank form error / toast.
 */
export function resolveErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  if (typeof error === "string" && error.trim()) return error;
  return fallback;
}

export type ValidationErrors = Record<string, string>;

export interface ShopDomainErrorMessages {
  enterDomain: string;
  enterAccessToken: string;
  enterAuthKey: string;
  invalidShopifyDomain: string;
  invalidWooCommerceDomain: string;
}

const shopifyShopSchema = (messages: ShopDomainErrorMessages) =>
  z
    .string()
    .trim()
    .min(1, { message: messages.enterDomain })
    .transform(normaliseShopifyShop)
    .refine((value) => value.length > 0 && SHOPIFY_STORE_NAME_PATTERN.test(value), {
      message: messages.invalidShopifyDomain,
    });

const wooCommerceShopSchema = (messages: ShopDomainErrorMessages) =>
  z
    .string()
    .trim()
    .min(1, { message: messages.enterDomain })
    .transform(normaliseWooCommerceShop)
    .refine((value) => DOMAIN_PATTERN.test(value.replace(HTTP_PROTOCOL_PATTERN, "")), {
      message: messages.invalidWooCommerceDomain,
    });

/**
 * Build a Zod schema for the shop-domain form based on the active service.
 * The schema normalises (`trim`, strip suffix/trailing slash) and validates
 * in one pass; `safeParse(formData)` returns either the normalised payload
 * or a flat `{ fieldName: message }` error map.
 */
export function buildShopDomainSchema(service: Service, messages: ShopDomainErrorMessages) {
  const shape: Record<string, z.ZodTypeAny> = {};

  if (service.id === "shopify") {
    shape.shop = shopifyShopSchema(messages);
    shape.accessToken = z.string().trim().min(1, { message: messages.enterAccessToken });
  } else if (service.id === "woocommerce") {
    // WooCommerce authenticates via the install/OAuth flow, so the only
    // user-supplied field is the shop domain — there is no auth key.
    shape.shop = wooCommerceShopSchema(messages);
  } else {
    shape.shop = z.string().trim();
  }

  return z.object(shape).passthrough();
}

/**
 * Flatten a ZodError into `{ fieldName: firstErrorMessage }`. We keep only
 * the first error per field — the UI renders one message per input.
 */
export function flattenZodErrors(error: z.ZodError): ValidationErrors {
  const fieldErrors: ValidationErrors = {};
  for (const issue of error.issues) {
    const key = issue.path[0];
    if (typeof key === "string" && fieldErrors[key] === undefined) {
      fieldErrors[key] = issue.message;
    }
  }
  return fieldErrors;
}
